/**
 * The operator screen (container, PLAN §5.2–5.3, D3, D19, D25): the live
 * playout session (`api/playout.ts`) in, the presentational playout
 * components out. It owns the page's UI state — the part cursor, the tab —
 * and turns key presses (`lib/playout/hotkeys.ts` → `commands.ts`) and the
 * components' events into commands. Commands are never optimistic: the TAKE
 * button shows pending until the ack, a nack is a toast with its reason.
 */
import type { PlayoutState } from '@acp/api-contract/playout.js';
import type { Production } from '@acp/api-contract/productions.js';
import { DEFAULT_MINIMUM_TAKE_SPAN_MS, type ProductionConfig } from '@acp/engine/productionConfig.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { PlayoutNotice, PlayoutSession, PlayoutSnapshot } from '../api/playout.js';
import { useAsRun, useGrants, useNow, usePlayoutSession, usePlayoutSnapshot, useServices, useSwitchover } from '../app/hooks.js';
import { AdlibPanel, type PieceTypeChoice } from '../components/playout/AdlibPanel.js';
import { AsRunList } from '../components/playout/AsRunList.js';
import { DevicePanel } from '../components/playout/DevicePanel.js';
import { OnAirUpdateBanner } from '../components/playout/OnAirUpdateBanner.js';
import { PairBadge } from '../components/playout/PairBadge.js';
import { PlayoutControls } from '../components/playout/PlayoutControls.js';
import { PlayoutHeader, type PlayoutHeaderProps } from '../components/playout/PlayoutHeader.js';
import { PlayoutRundown } from '../components/playout/PlayoutRundown.js';
import { TakeButton } from '../components/playout/TakeButton.js';
import { Button } from '../components/ui/button.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { actionsOf, adlibPanelsOf } from '../lib/playout/adlib.js';
import { lastTakeOf, liveTiming, productionNow } from '../lib/playout/clock.js';
import { type CommandInput, intentOutcome } from '../lib/playout/commands.js';
import { DEFAULT_BINDINGS, type FocusKind, type HotkeyContext, type Intent, resolveHotkey, type TakeGuard, takeGuard, takeView } from '../lib/playout/hotkeys.js';
import { definitionIn, type LiveEdit, pendingLiveEdit } from '../lib/playout/liveEdit.js';
import { type PartView, playoutView, type PlayoutView as PlayoutViewModel, sourceLayersOf } from '../lib/playout/viewModel.js';
import { allows } from '../lib/permissions.js';

/** How often the countdowns re-render. */
const TICK_MS = 250;

/** The keys shown on the TAKE button. */
const TAKE_KEYS = DEFAULT_BINDINGS.filter((b) => b.action === 'take' && b.anyFocus).map((b) => b.combo);

function notifyToast(notice: PlayoutNotice): void {
  if (notice.level === 'error') toast.error(notice.message);
  else toast(notice.message);
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** The input types a user types into. */
const TYPING_INPUTS: ReadonlySet<string> = new Set(['text', 'search', 'number', 'email', 'url', 'tel', 'password', 'date', 'time', 'datetime-local']);

/** Where a key press landed. A dialog owns its keys (a confirmation's Enter is not a take); typing is never a hotkey. */
function focusKind(target: EventTarget | null): FocusKind {
  if (!(target instanceof HTMLElement)) return 'none';
  if (target.closest('[role="dialog"], [role="alertdialog"]') !== null) return 'text';
  if (target.isContentEditable || target.tagName === 'TEXTAREA') return 'text';
  // Only typing is text: a checkbox, a radio or a dropdown is a control (F12 and the item hotkeys still fire there).
  if (target instanceof HTMLInputElement) return TYPING_INPUTS.has(target.type) ? 'text' : 'control';
  return target.closest('button, a, select, [role="tab"]') === null ? 'none' : 'control';
}

export function PlayoutView({ productionId }: { readonly productionId: string }) {
  const session = usePlayoutSession(productionId, notifyToast);
  const snap = usePlayoutSnapshot(session);
  if (snap.status === 'failed') {
    return (
      <div className="mx-auto grid max-w-xl gap-4 p-6" role="alert" data-testid="playout-failed">
        <p className="text-sm text-destructive">Could not open the production: {snap.error}</p>
        <Button variant="outline" onClick={() => session.retry()} className="w-fit">
          Retry
        </Button>
      </div>
    );
  }
  if (snap.production === null) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground" data-testid="playout-loading">
        Loading the production…
      </p>
    );
  }
  return <PlayoutScreen session={session} snap={snap} production={snap.production} />;
}

function AsRunTab({ productionId }: { readonly productionId: string }) {
  const asRun = useAsRun(productionId);
  return (
    <div className="p-4">
      <AsRunList
        events={(asRun.data?.pages ?? []).flatMap((p) => p.events)}
        loading={asRun.isFetching}
        error={asRun.error === null ? null : messageOf(asRun.error)}
        hasMore={asRun.hasNextPage}
        onLoadMore={() => {
          void asRun.fetchNextPage();
        }}
        onRefresh={() => {
          void asRun.refetch();
        }}
      />
    </div>
  );
}

/** What the config snapshot offers the screen: lanes, panels, actions, piece types. */
function useConfigViews(config: ProductionConfig | null) {
  return useMemo(() => {
    if (config === null) return { layers: [], panels: [], actions: [], pieceTypes: [] };
    const pieceTypes: PieceTypeChoice[] = Object.entries(config.pieceTypes).map(([id, t]) => ({ id, label: t.label === '' ? id : t.label, params: t.params }));
    return { layers: sourceLayersOf(config.sourceLayers), panels: adlibPanelsOf(config), actions: actionsOf(config), pieceTypes };
  }, [config]);
}

function partsOf(view: PlayoutViewModel): PartView[] {
  return view.rundowns.flatMap((r) => r.segments.flatMap((s) => s.parts));
}

/** The take guard over the snapshot, at the browser's `now`. */
function guardOf(snap: PlayoutSnapshot, config: ProductionConfig | null, permitted: boolean, now: number): TakeGuard {
  const state = snap.state;
  return takeGuard({
    active: state !== null && state.activation.status !== 'inactive',
    hasNext: (state?.next ?? null) !== null,
    pending: snap.pending.some((p) => p.type === 'take'),
    lastTakeTime: state === null ? null : lastTakeOf(state),
    productionNowMs: state === null ? 0 : productionNow(state.timing, snap.stateAt, now),
    minimumTakeSpanMs: config?.settings.minimumTakeSpanMs ?? DEFAULT_MINIMUM_TAKE_SPAN_MS,
    permitted,
  });
}

/**
 * One key listener for the page's life. It reads the latest render's
 * context through a ref, updated after each render.
 */
function useHotkeys(current: { readonly context: HotkeyContext; readonly selected: string | null; readonly enabled: boolean; readonly run: (intent: Intent) => void }): void {
  const latest = useRef(current);
  useEffect(() => {
    latest.current = current;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const now = latest.current;
      if (!now.enabled) return;
      const press = { key: event.key, code: event.code, shift: event.shiftKey, ctrl: event.ctrlKey, alt: event.altKey, meta: event.metaKey, repeat: event.repeat, focus: focusKind(event.target) };
      const { intent } = resolveHotkey(press, now.context, now.selected);
      if (intent === null) return;
      event.preventDefault();
      now.run(intent);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

type BannersProps = {
  readonly production: Production;
  readonly snap: PlayoutSnapshot;
  readonly liveEdit: LiveEdit | null;
  readonly canTake: boolean;
  readonly send: (command: CommandInput) => void;
  readonly onRetry: () => void;
};

function Banners({ production, snap, liveEdit, canTake, send, onRetry }: BannersProps) {
  const current = snap.state?.current ?? null;
  return (
    <>
      {production.status === 'ended' ? (
        <div role="status" className="border-b bg-muted px-4 py-2 text-sm" data-testid="production-ended">
          This production has ended{production.endReason === null ? '' : `: ${production.endReason}`}. It takes no more commands.
        </div>
      ) : null}
      {snap.stateError === null ? null : (
        <div role="alert" className="flex items-center gap-3 border-b border-destructive px-4 py-2 text-sm text-destructive" data-testid="playout-state-error">
          <span className="flex-1">No live playout state: {snap.stateError}</span>
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="playout-retry">
            Retry
          </Button>
        </div>
      )}
      {snap.configError === null ? null : (
        <div role="alert" className="border-b border-warning bg-warning px-4 py-2 text-sm text-warning-foreground">
          The config snapshot does not parse; adlibs and actions are unavailable. {snap.configError}
        </div>
      )}
      {liveEdit === null || current === null ? null : (
        <OnAirUpdateBanner
          partTitle={current.instance.part.title}
          fields={liveEdit.fields}
          canUpdate={canTake}
          pending={snap.pending.some((p) => p.type === 'syncToInstance')}
          onUpdate={() => send({ type: 'syncToInstance', partInstanceId: liveEdit.partInstanceId, fields: [...liveEdit.fields] })}
        />
      )}
    </>
  );
}

/** The main/backup pair (PLAN §8.6), when the production runs in one: who is active, and the manual switchover. */
function Pair({ production, state, canSwitch }: { readonly production: Production; readonly state: PlayoutState | null; readonly canSwitch: boolean }) {
  const switchover = useSwitchover();
  const redundancy = state?.redundancy;
  if (redundancy === undefined) return null;
  const onSwitchover = (): void => {
    switchover.mutate(
      { productionId: production.productionId },
      {
        onSuccess: (result) => toast(`Switched over: ${result.from} → ${result.to} (term ${String(result.term)}) in ${String(result.durationMs)} ms`),
        onError: (err) => toast.error(`Switchover refused: ${messageOf(err)}`),
      },
    );
  };
  return <PairBadge redundancy={redundancy} canSwitch={canSwitch} switching={switchover.isPending} onSwitchover={onSwitchover} />;
}

type Clock = { readonly elapsedMs: number; readonly remainingMs: number | null };

/** The clock bar's props from the live timings and the view. */
function headerOf(snap: PlayoutSnapshot, production: Production, timing: PlayoutState['timing'] | null, parts: readonly PartView[], onAirClock: Clock | null): PlayoutHeaderProps {
  const onAirTitle = parts.find((p) => p.status === 'onair')?.title ?? null;
  return {
    productionLabel: `${snap.rundowns.map((r) => r.name).join(' · ')} · ${production.studioId}`,
    activation: snap.state?.activation.status ?? 'inactive',
    onAir: onAirClock === null || onAirTitle === null ? null : { title: onAirTitle, ...onAirClock },
    nextTitle: parts.find((p) => p.status === 'next')?.title ?? null,
    mode: timing?.mode ?? 'none',
    remainingMs: timing?.remainingMs ?? 0,
    overUnderMs: timing?.overUnderMs ?? null,
    expectedEnd: timing?.expectedEnd ?? null,
    projectedEnd: timing?.projectedEnd ?? null,
    connection: snap.connection,
  };
}

export type ScreenProps = { readonly session: PlayoutSession; readonly snap: PlayoutSnapshot; readonly production: Production };

/** The live screen, once the production is loaded (exported for the unit layer's render). */
export function PlayoutScreen({ session, snap, production }: ScreenProps) {
  const grants = useGrants();
  const now = useNow(TICK_MS);
  const { nowMs } = useServices();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState('playout');
  const { config, state } = snap;
  const resource = { siteId: production.siteId, studioId: production.studioId };
  const ended = production.status === 'ended';
  const canTake = !ended && allows(grants, 'playout.take', resource);
  const canActivate = !ended && allows(grants, 'playout.activate', resource) && state !== null;
  const active = state !== null && state.activation.status !== 'inactive';
  const canCommand = canTake && active;

  const { layers, panels, actions, pieceTypes } = useConfigViews(config);
  const current = state?.current ?? null;
  const onAirContext = current === null ? null : { segmentId: current.instance.segmentId, untimed: current.instance.part.untimed };
  const timing = state === null ? null : liveTiming(state.timing, snap.stateAt, now, onAirContext);
  // Rebuilt every tick: the segments' played time and pace move with the on-air clock.
  const view = playoutView(snap.rundowns, state === null || timing === null ? state : { ...state, timing }, layers);
  const guard = guardOf(snap, config, canTake, now);
  // D19: the on-air part's definition moved away from its instance in a live-editable field.
  // `snap.rundowns` is a new array whenever a row changed: the definition may have.
  const liveEdit = useMemo(
    () => (current === null ? null : pendingLiveEdit(current.instance.instanceId, current.instance.part, definitionIn(snap.rundowns, current.instance.partId))),
    [current, snap.rundowns],
  );

  const send = (command: CommandInput): void => void session.send(command);
  const run = (intent: Intent): void => {
    // The guard on the session's state as it is at the key press, not as last rendered: a second press in the
    // same frame must see the first take pending.
    const takeAllowed = intent.type === 'take' && guardOf(session.snapshot(), config, canTake, nowMs()).allowed;
    const outcome = intentOutcome(intent, { takeAllowed, canCommand, panels });
    if (outcome.kind === 'select') setSelected(outcome.partId);
    else if (outcome.kind === 'command') send(outcome.command);
  };
  useHotkeys({
    context: {
      bindings: DEFAULT_BINDINGS,
      items: panels.flatMap((panel) => panel.items.flatMap((item, index) => (item.hotkey === null ? [] : [{ hotkey: item.hotkey, target: { panelId: panel.id, index } }]))),
      partOrder: view.partOrder,
      segmentOf: view.segmentOf,
      anchorPartId: view.nextPartId ?? view.onAirPartId,
    },
    selected,
    enabled: tab === 'playout',
    run,
  });

  const onAir = timing?.onAir ?? null;
  const onAirClock = onAir === null ? null : { elapsedMs: onAir.elapsedMs, remainingMs: onAir.remainingMs };
  const header = headerOf(snap, production, timing, partsOf(view), onAirClock);

  return (
    <div className="flex h-full flex-col" data-testid="playout-view">
      <PlayoutHeader {...header} />
      <Pair production={production} state={state} canSwitch={!ended && allows(grants, 'node.switchover', resource)} />
      <Banners production={production} snap={snap} liveEdit={liveEdit} canTake={canTake} send={send} onRetry={() => session.retry()} />
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col gap-0">
        <TabsList className="mx-4 mt-2">
          <TabsTrigger value="playout">Playout</TabsTrigger>
          <TabsTrigger value="asrun" data-testid="asrun-tab">
            As-run
          </TabsTrigger>
        </TabsList>
        <TabsContent value="playout" className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-y-auto">
            <PlayoutRundown
              view={view}
              selectedPartId={selected}
              onAirClock={onAirClock}
              canCommand={canCommand}
              onSelect={setSelected}
              onSetNext={(partId) => send({ type: 'setNext', partId })}
              onSetNextSegment={(segmentId) => send({ type: 'setNextSegment', segmentId })}
              onQueueSegment={(segmentId) => send({ type: 'queueNextSegment', segmentId })}
            />
          </div>
          <aside className="grid w-80 shrink-0 content-start gap-5 overflow-y-auto border-l bg-card p-3" aria-label="Playout controls">
            <TakeButton state={takeView(guard, snap.lastOutcome?.type === 'take' ? snap.lastOutcome : null)} nextTitle={header.nextTitle} hotkeys={TAKE_KEYS} onTake={() => run({ type: 'take' })} />
            <PlayoutControls
              status={state?.activation.status ?? 'inactive'}
              rehearsalProduction={production.rehearsal}
              canActivate={canActivate}
              canTake={canTake}
              pending={snap.pending.some((p) => p.type === 'activate' || p.type === 'deactivate' || p.type === 'reset')}
              onActivate={(rehearsal) => send({ type: 'activate', rehearsal })}
              onDeactivate={() => send({ type: 'deactivate' })}
              onReset={() => send({ type: 'reset' })}
              onMoveNext={(delta) => send({ type: 'moveNext', delta })}
              onDisableNextPiece={() => send({ type: 'disableNextPiece' })}
            />
            <AdlibPanel
              panels={panels}
              actions={actions}
              pieceTypes={pieceTypes}
              layers={layers}
              enabled={canCommand}
              onAdlib={(request) => send({ type: 'adlib', ...request })}
              onAction={(actionId) => send({ type: 'executeAction', actionId })}
              onStopLayer={(layer) => send({ type: 'stopPiecesOnLayer', layer })}
            />
            <section aria-label="Devices" className="grid gap-2">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Devices</h3>
              <DevicePanel devices={snap.devices} available={state !== null} nowMs={now} />
            </section>
          </aside>
        </TabsContent>
        <TabsContent value="asrun" className="min-h-0 flex-1 overflow-y-auto">
          <AsRunTab productionId={production.productionId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
